import { Injectable } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ItemDetailsService } from '../services/item-details.service';
import { LoaderService } from '../services/loader.service';
import { calculateRowAmounts, normalizeCreditNoteQuantity } from './item-utils';
import { refreshOrderLinesStockErrors, STOCK_CHECK_EXCLUDED_ITEMS } from './utils/order-line-stock.util';

/**
 * Ports quantity blur / stock API / validation from item-table (V1) for reuse in V2 lines grid.
 * Stock refresh uses lean m3/item/available-stock (same as recommendation cards), not full Item Details.
 */
export interface GridQuantityBlurContext {
    row: any;
    itemList: any[];
    itemListIndex: number;
    displayIndex: number;
    oldQty: number;
    company: string;
    division: string;
    warehouse: string;
    customerId: string;
    deletedItems: Map<string, any[]>;
    ruleActiveForDivision: boolean;
    emitTableChange: () => void;
    deleteItemAtListIndex: (idx: number) => void;
    focusQuantityAtDisplayIndex: (idx: number) => void;
    isCreditNoteOrder?: boolean;
    /** Optional hook after qty/stock handling completes (e.g. margin validation in V2 grid). */
    onAfterStockResolved?: () => void;
}

/** Provide next to `ConfirmationService` on the grid component so confirm + stock share one injector. */
@Injectable()
export class GridStockService {
    private readonly stockUpdateInProgress = new Map<string, boolean>();

    constructor(
        private readonly itemDetailsService: ItemDetailsService,
        private readonly loaderService: LoaderService,
        private readonly messageService: MessageService,
        private readonly confirmationService: ConfirmationService
    ) {}

    /** Same as item-table previousQty (focus) — capture originalQuantity when rules apply */
    onQuantityFocus(row: any, ruleActiveForDivision: boolean): void {
        if (ruleActiveForDivision) {
            if (row.status === 20 && row.originalQuantity === undefined) {
                row.originalQuantity = row.quantity;
            } else if (row.status === 30 && row.originalQuantity === undefined) {
                row.originalQuantity = row.quantity;
            }
        }
    }

    handleQuantityBlur(ctx: GridQuantityBlurContext): void {
        const row = ctx.row;
        const rowKey = `${row.itemNumber}_${row.lineNumber}`;
        const isCreditNote = !!ctx.isCreditNoteOrder;

        if (!isCreditNote && row.quantity < 0) {
            row.quantity = 0;
        }

        if (this.stockUpdateInProgress.get(rowKey)) {
            Object.assign(row, calculateRowAmounts(row));
            if (isCreditNote) {
                row.quantity = normalizeCreditNoteQuantity(Number(row.quantity));
            }
            return;
        }

        const absQty = Math.abs(Number(row.quantity) || 0);
        if (absQty <= 0.9) {
            this.handleZeroQuantity(ctx);
            return;
        }

        // Skip stock + post-hooks when quantity did not change (including status 10).
        // Do not call onAfterStockResolved here — that used to re-trigger ItemDetails for margin.
        const prevQty = Number(ctx.oldQty);
        const nextQty = Number(row.quantity);
        if (Number.isFinite(prevQty) && Number.isFinite(nextQty) && prevQty === nextQty) {
            Object.assign(row, calculateRowAmounts(row));
            ctx.emitTableChange();
            return;
        }

        this.updateRowStatus(ctx);
        Object.assign(row, calculateRowAmounts(row));
        if (isCreditNote) {
            row.quantity = normalizeCreditNoteQuantity(Number(row.quantity));
            ctx.emitTableChange();
            ctx.onAfterStockResolved?.();
            return;
        }
        this.stockUpdateInProgress.set(rowKey, true);
        this.updateStockForRow(ctx);
    }

    private handleZeroQuantity(ctx: GridQuantityBlurContext): void {
        const row = ctx.row;
        const previousQty = ctx.oldQty > 0 ? ctx.oldQty : row.quantity || 0;

        this.confirmationService.confirm({
            header: 'Zero Quantity Entered',
            message: 'Quantity cannot be zero. Do you want to delete this article?',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Delete',
            rejectLabel: 'No, Keep',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-secondary',
            accept: () => {
                ctx.deleteItemAtListIndex(ctx.itemListIndex);
            },
            reject: () => {
                row.quantity = previousQty;
                ctx.emitTableChange();
                setTimeout(() => ctx.focusQuantityAtDisplayIndex(ctx.displayIndex), 100);
            },
            dismissableMask: true,
            closeOnEscape: true
        });
    }

    private updateRowStatus(ctx: GridQuantityBlurContext): void {
        const row = ctx.row;
        const oldQty = ctx.oldQty;

        if (row.status === 20) {
            if (row.originalQuantity === undefined) {
                if (oldQty > 0) {
                    row.originalQuantity = oldQty;
                    row.originalStatus = 20;
                } else {
                    row.originalQuantity = row.quantity;
                    row.originalStatus = 20;
                }
            } else if (row.originalStatus === undefined) {
                row.originalStatus = 20;
            }
            row.originalQuantityWasSaved = true;
            row.status = 30;
        } else if (row.status === 10) {
            if (row.originalQuantity === undefined) {
                row.originalQuantity = oldQty > 0 ? oldQty : Number(row.quantity);
            }
            if (row.originalStatus === undefined) {
                row.originalStatus = 10;
            }
            row.originalQuantityWasSaved = false;
            row.status = 30;
        } else if (row.status === 30) {
            if (row.originalQuantity === undefined) {
                row.originalQuantity = oldQty > 0 ? oldQty : row.quantity;
                row.originalQuantityWasSaved = row.originalQuantityWasSaved !== undefined ? row.originalQuantityWasSaved : false;
            }
            if (row.originalQuantityWasSaved === undefined) {
                row.originalQuantityWasSaved = false;
            }
        } else {
            // Lines without status (e.g. partial API payload): treat like unsaved so stock validation runs.
            if (row.originalQuantity === undefined) {
                row.originalQuantity = oldQty > 0 ? oldQty : Number(row.quantity);
            }
            if (row.originalStatus === undefined) {
                row.originalStatus = 10;
            }
            row.originalQuantityWasSaved = false;
            row.status = 30;
        }

        ctx.emitTableChange();
        localStorage.setItem('orderLines', JSON.stringify(ctx.itemList));
    }

    private updateStockForRow(ctx: GridQuantityBlurContext): void {
        const row = ctx.row;
        const rowKey = `${row.itemNumber}_${row.lineNumber}`;

        if (!ctx.company || !ctx.division || !row.itemNumber) {
            this.stockUpdateInProgress.delete(rowKey);
            return;
        }

        if (STOCK_CHECK_EXCLUDED_ITEMS.has(String(row.itemNumber ?? '').trim())) {
            const stock = Math.max(0, Number(row.apiStock ?? row.avlStock) || 0);
            row.avlStock = stock;
            row.apiStock = stock;
            row.stockLoading = false;
            row.stockError = false;
            this.stockUpdateInProgress.delete(rowKey);
            this.notifyStockAvailabilityToast(row);
            this.validateQuantityAfterStockUpdate(ctx);
            this.resyncStockErrorsAfterQtyChange(ctx);
            ctx.emitTableChange();
            return;
        }

        const existingStock = row.avlStock !== undefined && row.avlStock !== null ? Number(row.avlStock) : null;
        row.stockLoading = true;
        row.stockError = false;

        const body = {
            itno: row.itemNumber,
            whlo: ctx.warehouse || ''
        };

        this.loaderService.show('Loading stock...', 'Please wait...');
        this.itemDetailsService.getAvailableStock(ctx.company, ctx.division, body, 'GRID_QTY_STOCK').subscribe({
            next: (response: any) => {
                this.loaderService.hide();
                const itemStockCount =
                    response?.itemStockCount ??
                    response?.ItemDetails?.itemStockCount ??
                    response?.ItemDetails?.avlStock;
                const apiStock = itemStockCount !== undefined && itemStockCount !== null && itemStockCount !== ''
                    ? Number(itemStockCount)
                    : null;

                if (apiStock === null || isNaN(apiStock)) {
                    row.stockLoading = false;
                    row.stockError = true;
                    if (existingStock !== null) {
                        row.avlStock = existingStock;
                    }
                    this.stockUpdateInProgress.delete(rowKey);
                    if (row.avlStock !== undefined && row.avlStock !== null) {
                        this.notifyStockAvailabilityToast(row);
                        this.validateQuantityAfterStockUpdate(ctx);
                    } else {
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Stock Unavailable',
                            detail: `Could not load stock for ${row.itemNumber}.`,
                            life: 5000
                        });
                        ctx.onAfterStockResolved?.();
                    }
                    this.resyncStockErrorsAfterQtyChange(ctx);
                    ctx.emitTableChange();
                    return;
                }

                row.apiStock = apiStock;
                const localStockToSubtract = this.calculateLocalStockForDisplay(ctx.itemList, row.itemNumber, row);
                const adjustedStock = Math.max(0, apiStock - localStockToSubtract);

                if (adjustedStock >= 0) {
                    row.avlStock = adjustedStock;
                    row.stockLoading = false;
                    row.stockError = false;
                } else {
                    if (existingStock !== null) {
                        row.avlStock = existingStock;
                    }
                    row.stockLoading = false;
                    row.stockError = true;
                }

                this.stockUpdateInProgress.delete(rowKey);
                this.notifyStockAvailabilityToast(row);
                this.validateQuantityAfterStockUpdate(ctx);
                this.resyncStockErrorsAfterQtyChange(ctx);
                ctx.emitTableChange();
            },
            error: () => {
                this.loaderService.hide();
                row.stockLoading = false;
                row.stockError = true;
                if (existingStock !== null) {
                    row.avlStock = existingStock;
                }
                this.stockUpdateInProgress.delete(rowKey);
                if (row.avlStock !== undefined && row.avlStock !== null) {
                    this.notifyStockAvailabilityToast(row);
                    this.validateQuantityAfterStockUpdate(ctx);
                } else {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Stock Unavailable',
                        detail: `Could not load stock for ${row.itemNumber}.`,
                        life: 5000
                    });
                }
                this.resyncStockErrorsAfterQtyChange(ctx);
                ctx.emitTableChange();
                ctx.onAfterStockResolved?.();
            }
        });
    }

    /** Keep Excel/copy stock banners in sync after grid qty/stock refresh. Soft: never throws. */
    private resyncStockErrorsAfterQtyChange(ctx: GridQuantityBlurContext): void {
        try {
            refreshOrderLinesStockErrors(ctx.itemList, {
                ruleActiveForDivision: ctx.ruleActiveForDivision,
                isCreditNoteOrder: !!ctx.isCreditNoteOrder,
                isStockCheckExcluded: (itemNumber) =>
                    STOCK_CHECK_EXCLUDED_ITEMS.has(String(itemNumber ?? '').trim())
            });
        } catch {
            // ignore — save gate will re-evaluate
        }
    }

    /**
     * V1 item-table (commented block in updateStockForRow): toast when line stock is 0 vs available.
     */
    private notifyStockAvailabilityToast(row: any): void {
        if (row.avlStock === undefined || row.avlStock === null) {
            return;
        }
        const n = Number(row.avlStock);
        if (n === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Stock Unavailable',
                detail: `No stock available for ${row.itemNumber}. Current stock: 0`,
                life: 5000
            });
        } else {
            this.messageService.add({
                severity: 'success',
                summary: 'Total Stock',
                detail: `Total Stock for ${row.itemNumber} is ${n}`,
                life: 5000
            });
        }
    }

    private calculateLocalStockForDisplay(itemList: any[], itemNumber: string, _currentRow: any): number {
        let totalToSubtract = 0;
        const allItems = itemList.filter((item) => item.itemNumber === itemNumber);

        allItems.forEach((item) => {
            if (item.status === 10) {
                totalToSubtract += Number(item.quantity) || 0;
            } else if (item.status === 30) {
                totalToSubtract += Number(item.quantity) || 0;
            }
        });

        return totalToSubtract;
    }

    private calculateLocalStock(itemList: any[], itemNumber: string, _currentRow: any): number {
        let totalToSubtract = 0;
        const allItems = itemList.filter((item) => item.itemNumber === itemNumber);

        allItems.forEach((item) => {
            if (item.status === 10) {
                totalToSubtract += Number(item.quantity) || 0;
            } else if (item.status === 30) {
                const originalQty = item.originalQuantity !== undefined ? Number(item.originalQuantity) : Number(item.quantity);
                const currentQty = Number(item.quantity) || 0;
                const originalStatus = item.originalStatus;

                if (originalStatus === 10) {
                    totalToSubtract += currentQty - originalQty;
                } else if (originalStatus === 20) {
                    totalToSubtract += currentQty - originalQty;
                } else {
                    if (item.originalQuantityWasSaved === false || item.originalQuantityWasSaved === undefined) {
                        totalToSubtract += currentQty - originalQty;
                    } else {
                        totalToSubtract += currentQty - originalQty;
                    }
                }
            }
        });

        return totalToSubtract;
    }

    private validateQuantityAfterStockUpdate(ctx: GridQuantityBlurContext): void {
        const row = ctx.row;

        if (ctx.isCreditNoteOrder) {
            return;
        }

        if (!ctx.ruleActiveForDivision || (row.ar != 1 && row.ar !== undefined && row.ar !== null)) {
            ctx.onAfterStockResolved?.();
            return;
        }

        if (row.status === 30) {
            const originalQty = Number(row.originalQuantity !== undefined ? row.originalQuantity : ctx.oldQty) || 0;
            const currentQty = Number(row.quantity) || 0;
            const increase = currentQty - originalQty;

            if (increase <= 0) {
                this.messageService.add({
                    severity: 'info',
                    summary: 'Quantity Updated',
                    detail: `Quantity decreased by ${increase} units. Quantity set to ${currentQty} units.`,
                    life: 7000,
                    key: 'custom-toast'
                });
                ctx.onAfterStockResolved?.();
                return;
            }

            let apiStock: number;
            if (row.apiStock !== undefined && row.apiStock !== null) {
                apiStock = row.apiStock;
                const deletedItemsForThisItem = ctx.deletedItems.get(row.itemNumber) || [];
                let totalToAddBack = 0;
                deletedItemsForThisItem.forEach((deletedItem) => {
                    if (deletedItem.status === 20) {
                        totalToAddBack += deletedItem.quantity;
                    } else if (deletedItem.status === 30 && deletedItem.originalQuantityWasSaved === true) {
                        const oq = deletedItem.originalQuantity !== undefined ? deletedItem.originalQuantity : deletedItem.quantity;
                        totalToAddBack += oq;
                    }
                });
                if (totalToAddBack > 0) {
                    apiStock = apiStock + totalToAddBack;
                }
            } else {
                const localStockWithNewQty = this.calculateLocalStock(ctx.itemList, row.itemNumber, row);
                apiStock = row.avlStock + localStockWithNewQty;
            }

            const tempCurrentQty = row.quantity;
            const tempStatus = row.status;
            const tempOriginalStatus = row.originalStatus;

            if (row.originalStatus === 10) {
                row.quantity = originalQty;
                row.status = 10;
                row.originalStatus = undefined;
            } else {
                row.quantity = originalQty;
            }

            const localStockWithOriginal = this.calculateLocalStockForDisplay(ctx.itemList, row.itemNumber, row);
            row.quantity = tempCurrentQty;
            row.status = tempStatus;
            row.originalStatus = tempOriginalStatus;

            let availableStockBeforeChange: number;
            if (row.originalStatus === 20) {
                availableStockBeforeChange = Math.max(0, apiStock + originalQty - localStockWithOriginal);
            } else {
                availableStockBeforeChange = Math.max(0, apiStock - localStockWithOriginal);
            }

            if (increase > availableStockBeforeChange) {
                const maxQuantity = availableStockBeforeChange + originalQty;
                row.quantityError = `Max available stock is ${maxQuantity}`;
                row.quantity = ctx.oldQty;
                Object.assign(row, calculateRowAmounts(row));
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Insufficient Stock',
                    detail: `You tried to increase quantity by ${increase} units, but only ${availableStockBeforeChange} units are available. Maximum quantity you can set: ${maxQuantity} (current: ${originalQty} + available: ${availableStockBeforeChange} = ${maxQuantity})`,
                    life: 7000,
                    key: 'custom-toast'
                });
                ctx.emitTableChange();
                localStorage.setItem('orderLines', JSON.stringify(ctx.itemList));
                ctx.onAfterStockResolved?.();
                return;
            }

            const maxQuantity = availableStockBeforeChange + originalQty;
            this.messageService.add({
                severity: 'info',
                summary: 'Quantity Updated',
                detail: `Quantity increased by ${increase} units.Total qty entered ${row.quantity} Maximum quantity: ${maxQuantity}`,
                life: 7000,
                key: 'custom-toast'
            });
            ctx.onAfterStockResolved?.();
            return;
        }

        if (row.quantity > row.avlStock) {
            const attemptedQty = row.quantity;
            row.quantityError = `Max available stock is ${row.avlStock}`;
            row.quantity = ctx.oldQty;
            Object.assign(row, calculateRowAmounts(row));
            this.messageService.add({
                severity: 'warn',
                summary: 'Insufficient Stock',
                detail: `You tried to set quantity to ${attemptedQty} units, but only ${row.avlStock} units are available. Quantity reverted to ${ctx.oldQty}`,
                life: 7000,
                key: 'custom-toast'
            });
            ctx.emitTableChange();
            localStorage.setItem('orderLines', JSON.stringify(ctx.itemList));
            ctx.onAfterStockResolved?.();
            return;
        }

        this.messageService.add({
            severity: 'info',
            summary: 'Quantity Updated',
            detail: `Quantity set to ${row.quantity} units. Available stock remaining: ${row.avlStock - row.quantity} units`,
            life: 7000,
            key: 'custom-toast'
        });
        ctx.onAfterStockResolved?.();
    }
}
